import React, { useState, useMemo, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Check from '@mui/icons-material/Check';
import CloudUpload from '@mui/icons-material/CloudUpload';
import CreateNewFolder from '@mui/icons-material/CreateNewFolder';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Folder from '@mui/icons-material/Folder';
import Home from '@mui/icons-material/Home';
import Image from '@mui/icons-material/Image';
import Search from '@mui/icons-material/Search';
import { ColumnDef } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { format } from 'date-fns/format';
import { Alert, Modal } from '@/ui';
import TanstackTable from '../Table/TanstackTable';
import { useImages, useUploadImage, useCreateFolder } from './queries';
import { MediaItem, MediaFolder } from './types';
import { validateFolderName, getApiErrorMessage } from './folderName';

interface MediaLibraryItem extends MediaItem {
  type: 'file';
}

interface MediaLibraryFolder extends MediaFolder {
  type: 'folder';
}

type MediaLibraryDisplayItem = MediaLibraryItem | MediaLibraryFolder;

export interface MediaLibraryCoreProps {
  onSelect?: (item: MediaItem) => void;
  selectedItem?: MediaItem | undefined;
  isSelectionMode?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const MediaLibraryCore: React.FC<MediaLibraryCoreProps> = ({
  onSelect,
  isSelectionMode = false,
  onUploadStart,
  onUploadComplete,
  className,
  style,
}) => {
  const [currentPath, setCurrentPath] = useState<string>('');
  const [pathHistory, setPathHistory] = useState<string[]>(['']);
  const [historyIndex, setHistoryIndex] = useState<number>(0);
  const [selectedPreviewItem, setSelectedPreviewItem] = useState<MediaItem | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [uploadConfirmation, setUploadConfirmation] = useState<{
    isOpen: boolean;
    files: File[];
  }>({ isOpen: false, files: [] });
  // Optional folder to create/upload into, entered in the upload confirmation
  const [uploadFolderName, setUploadFolderName] = useState<string>('');
  // Separate "New Folder" flow, for creating a folder with no documents in it
  const [newFolder, setNewFolder] = useState<{ isOpen: boolean; name: string }>({
    isOpen: false,
    name: '',
  });

  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaLibraryResponse, isLoading, error } = useImages(currentPath);
  const uploadImageMutation = useUploadImage();
  const createFolderMutation = useCreateFolder();

  // Validation for the two folder-name entry points. Both are optional until submitted, so an
  // empty value is not an error in the upload flow.
  const uploadFolderError = uploadFolderName.trim() ? validateFolderName(uploadFolderName) : null;
  const newFolderError = newFolder.name.trim() ? validateFolderName(newFolder.name) : null;

  // Target folder for an upload: the current folder, plus the optional new folder name
  const uploadTargetPath =
    uploadFolderName.trim() && !uploadFolderError
      ? `${currentPath}${uploadFolderName.trim()}/`
      : currentPath;

  // Navigation functions
  const navigateToPath = useCallback(
    (path: string) => {
      const newHistory = pathHistory.slice(0, historyIndex + 1);
      newHistory.push(path);
      setPathHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setCurrentPath(path);
      setSelectedPreviewItem(null);
    },
    [pathHistory, historyIndex]
  );

  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentPath(pathHistory[newIndex] || '');
      setSelectedPreviewItem(null);
    }
  }, [pathHistory, historyIndex]);

  const goForward = useCallback(() => {
    if (historyIndex < pathHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentPath(pathHistory[newIndex] || '');
      setSelectedPreviewItem(null);
    }
  }, [pathHistory, historyIndex]);

  const goHome = useCallback(() => {
    navigateToPath('');
  }, [navigateToPath]);

  // Combine items and folders for table display
  const displayItems: MediaLibraryDisplayItem[] = useMemo(() => {
    if (!mediaLibraryResponse) return [];

    const folders: MediaLibraryFolder[] = mediaLibraryResponse.folders.map((folder) => ({
      ...folder,
      type: 'folder' as const,
    }));

    const files: MediaLibraryItem[] = mediaLibraryResponse.items.map((item) => ({
      ...item,
      type: 'file' as const,
    }));

    return [...folders, ...files];
  }, [mediaLibraryResponse]);

  // Table columns configuration
  const columns: ColumnDef<MediaLibraryDisplayItem>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const item = row.original;
          const isFolder = item.type === 'folder';
          return (
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              {isFolder ? (
                <Folder fontSize='small' sx={{ color: 'warning.main' }} />
              ) : (
                <Image fontSize='small' sx={{ color: 'text.secondary' }} />
              )}
              <span>{isFolder ? item.name : (item as MediaItem).fileName}</span>
            </Stack>
          );
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Chip
            size='small'
            color={row.original.type === 'folder' ? 'warning' : 'info'}
            label={row.original.type === 'folder' ? 'Folder' : 'Image'}
          />
        ),
        enableColumnFilter: true,
        meta: {
          filterVariant: 'select',
        },
      },
      {
        accessorKey: 'lastModified',
        header: 'Last Modified',
        cell: ({ row }) =>
          (row.original as MediaItem).lastModified ? format((row.original as MediaItem).lastModified, 'Pp') : '',
        enableColumnFilter: true,
      },
    ],
    []
  );

  // Handle row selection
  const handleRowClick = useCallback(
    (event: React.MouseEvent, rowData: MediaLibraryDisplayItem) => {
      if (rowData.type === 'folder') {
        navigateToPath(rowData.fullPath);
      } else {
        setSelectedPreviewItem(rowData as MediaItem);
      }
    },
    [navigateToPath]
  );

  // Handle file selection
  const handleSelectFile = useCallback(() => {
    if (selectedPreviewItem && onSelect) {
      onSelect(selectedPreviewItem);
    }
  }, [selectedPreviewItem, onSelect]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'));
      if (files.length > 0) {
        setUploadConfirmation({ isOpen: true, files });
      }
    }
  }, []);

  // Handle file selection from file browser
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const files = Array.from(fileList);
      if (files.length > 0) {
        setUploadConfirmation({ isOpen: true, files });
      }
    }
    // Reset the input value to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  }, []);

  // Handle upload button click
  const handleUploadButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handle upload confirmation
  const handleUpload = useCallback(async () => {
    if (uploadConfirmation.files.length > 0) {
      if (uploadFolderError) {
        return;
      }
      onUploadStart?.();

      const targetPath = uploadTargetPath;

      const uploadPromise = async () => {
        for (const file of uploadConfirmation.files) {
          await uploadImageMutation.mutateAsync({ file, path: targetPath });
        }
        setUploadConfirmation({ isOpen: false, files: [] });
        setUploadFolderName('');
        onUploadComplete?.();
        // Follow the user into the folder they just created
        if (targetPath !== currentPath) {
          navigateToPath(targetPath);
        }
      };

      void toast.promise(
        uploadPromise(),
        {
          loading: 'Uploading files...',
          success: `Successfully uploaded ${uploadConfirmation.files.length} file(s)`,
          error: (err) => getApiErrorMessage(err, 'Upload failed'),
        },
        {
          className: 'zuul-toaster',
        }
      );
    }
  }, [
    uploadConfirmation.files,
    uploadTargetPath,
    uploadFolderError,
    currentPath,
    navigateToPath,
    uploadImageMutation,
    onUploadStart,
    onUploadComplete,
  ]);

  // Handle creating a folder with no documents in it
  const handleCreateFolder = useCallback(async () => {
    const name = newFolder.name.trim();
    if (!name || newFolderError) {
      return;
    }

    const createPromise = async () => {
      const folder = await createFolderMutation.mutateAsync({ path: currentPath, name });
      setNewFolder({ isOpen: false, name: '' });
      navigateToPath(folder.fullPath);
    };

    void toast.promise(
      createPromise(),
      {
        loading: 'Creating folder...',
        success: `Created folder "${name}"`,
        error: (err) => getApiErrorMessage(err, 'Could not create folder'),
      },
      {
        className: 'zuul-toaster',
      }
    );
  }, [newFolder.name, newFolderError, currentPath, createFolderMutation, navigateToPath]);

  // Generate breadcrumb items
  const breadcrumbItems = useMemo(() => {
    if (!currentPath) return [{ label: 'WhatIf Assets CDN', path: '' }];

    const pathParts = currentPath.split('/').filter(Boolean);
    const items = [{ label: 'WhatIf Assets CDN', path: '' }];

    // Keep the trailing slash so a crumb and a folder row produce the same path (and therefore
    // the same query cache key) for the same folder.
    let buildPath = '';
    pathParts.forEach((part) => {
      buildPath += `${part}/`;
      items.push({ label: part, path: buildPath });
    });

    return items;
  }, [currentPath]);

  return (
    <Box
      className={className}
      sx={{ position: 'relative', minHeight: 500 }}
      style={style}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {dragActive && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: theme.zIndex.modal - 1,
          })}
        >
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <CloudUpload sx={{ fontSize: '3rem', color: 'primary.main' }} />
            <Typography variant='h6'>Drop media items here to upload</Typography>
          </Stack>
        </Box>
      )}

      <Paper variant='outlined' sx={{ p: 1, mb: 2 }}>
        <Stack direction='row' spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title='Back'>
            <span>
              <IconButton onClick={goBack} disabled={historyIndex <= 0} aria-label='Back'>
                <ArrowBack />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Forward'>
            <span>
              <IconButton
                onClick={goForward}
                disabled={historyIndex >= pathHistory.length - 1}
                aria-label='Forward'
              >
                <ArrowForward />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Top level'>
            <IconButton onClick={goHome} aria-label='Top level'>
              <Home />
            </IconButton>
          </Tooltip>
          <Button color='success' variant='contained' startIcon={<CloudUpload />} onClick={handleUploadButtonClick}>
            Upload
          </Button>
          <Button
            color='inherit'
            variant='outlined'
            startIcon={<CreateNewFolder />}
            onClick={() => setNewFolder({ isOpen: true, name: '' })}
            title='Create a folder in the current folder'
          >
            New Folder
          </Button>
          <Breadcrumbs sx={{ ml: 1 }}>
            {breadcrumbItems.map((item, index) =>
              index === breadcrumbItems.length - 1 ? (
                <Typography key={index} sx={{ color: 'text.primary' }}>
                  {item.label}
                </Typography>
              ) : (
                <Link
                  key={index}
                  component='button'
                  type='button'
                  underline='hover'
                  color='inherit'
                  onClick={() => navigateToPath(item.path)}
                >
                  {item.label}
                </Link>
              )
            )}
          </Breadcrumbs>
        </Stack>
      </Paper>

      {Boolean(error) && (
        <Alert color='danger' sx={{ mb: 2 }}>
          {getApiErrorMessage(error, 'Error loading media library')}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Main Content Area */}
        <Grid size={{ xs: 12, md: selectedPreviewItem ? 8 : 12 }}>
          <TextField
            fullWidth
            size='small'
            placeholder='Search files and folders...'
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              htmlInput: { 'aria-label': 'Search files and folders' },
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Card>
            <TanstackTable
              columns={columns}
              data={displayItems || []}
              globalFilter={searchFilter}
              onGlobalFilterChange={setSearchFilter}
              rowEvents={{
                onClick: handleRowClick,
              }}
              pageSize={10}
              className='media-library-table'
              enableSearch={true} // We have our own search
              loading={isLoading}
            />
          </Card>
        </Grid>

        {/* Preview Panel */}
        {selectedPreviewItem && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 16 }}>
              <CardMedia
                component='img'
                src={selectedPreviewItem.url}
                alt={selectedPreviewItem.fileName}
                sx={{ height: 200, objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  {selectedPreviewItem.fileName}
                </Typography>
                <Typography variant='caption' sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                  ID: {selectedPreviewItem._id}
                </Typography>
                {isSelectionMode ? (
                  <Button fullWidth variant='contained' startIcon={<Check />} onClick={handleSelectFile}>
                    Select File
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant='contained'
                    startIcon={<ContentCopy />}
                    onClick={() => {
                      if (selectedPreviewItem) {
                        void navigator.clipboard
                          .writeText(selectedPreviewItem.url)
                          .then(() => toast.success('URL copied to clipboard'));
                      }
                    }}
                  >
                    Copy Cloudfront URL
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Hidden file input for upload functionality */}
      <input type='file' ref={fileInputRef} onChange={handleFileSelect} multiple style={{ display: 'none' }} />

      {/* Upload confirmation — a centred dialog rather than a corner toast, so the folder name
          field is where the eye already is */}
      <Modal
        isOpen={uploadConfirmation.isOpen}
        toggle={() => {
          setUploadConfirmation({ isOpen: false, files: [] });
          setUploadFolderName('');
        }}
        centered
        size='md'
        tone='warning'
        testId='upload-confirmation'
        title={`Upload ${uploadConfirmation.files.length} file(s)?`}
        footer={
          <>
            <Button
              variant='contained'
              color='success'
              onClick={handleUpload}
              disabled={uploadImageMutation.isPending || !!uploadFolderError}
              startIcon={uploadImageMutation.isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
            >
              Upload
            </Button>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => {
                setUploadConfirmation({ isOpen: false, files: [] });
                setUploadFolderName('');
              }}
              disabled={uploadImageMutation.isPending}
            >
              Cancel
            </Button>
          </>
        }
      >
        <Typography variant='body2'>You are about to upload:</Typography>
        <Box component='ul' sx={{ pl: 3, my: 1 }}>
          {uploadConfirmation.files.map((file, index) => (
            <li key={index}>{file.name}</li>
          ))}
        </Box>
        <TextField
          id='media-upload-folder-name'
          label='Folder name (optional)'
          placeholder='Leave blank to upload here'
          value={uploadFolderName}
          onChange={(e) => setUploadFolderName(e.target.value)}
          error={!!uploadFolderError}
          helperText={uploadFolderError ?? ' '}
          disabled={uploadImageMutation.isPending}
          size='small'
          fullWidth
          sx={{ mt: 1, mb: 1 }}
        />
        <Typography variant='body2'>
          to <strong>whatif-assets-cdn/{uploadTargetPath}</strong>
        </Typography>
      </Modal>

      {/* New Folder — creates a folder that contains no documents */}
      <Modal
        isOpen={newFolder.isOpen}
        toggle={() => setNewFolder({ isOpen: false, name: '' })}
        centered
        size='sm'
        tone='info'
        testId='new-folder'
        title='New folder'
        footer={
          <>
            <Button
              variant='contained'
              color='success'
              onClick={handleCreateFolder}
              disabled={createFolderMutation.isPending || !newFolder.name.trim() || !!newFolderError}
              startIcon={createFolderMutation.isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
            >
              Create
            </Button>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => setNewFolder({ isOpen: false, name: '' })}
              disabled={createFolderMutation.isPending}
            >
              Cancel
            </Button>
          </>
        }
      >
        <TextField
          id='media-new-folder-name'
          label='Folder name'
          autoFocus
          value={newFolder.name}
          onChange={(e) => setNewFolder((prev) => ({ ...prev, name: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void handleCreateFolder();
            }
          }}
          error={!!newFolderError}
          helperText={newFolderError ?? ' '}
          disabled={createFolderMutation.isPending}
          size='small'
          fullWidth
          sx={{ mb: 1 }}
        />
        <Typography variant='body2'>
          in <strong>whatif-assets-cdn/{currentPath}</strong>
        </Typography>
      </Modal>
    </Box>
  );
};

export default MediaLibraryCore;
